/* eslint-disable react-hooks/exhaustive-deps */
import React, {useContext, useEffect, useState} from "react"
import {Col, Row} from "antd"
import {get, isEmpty, keys, orderBy, pick} from "lodash"
import {FormLabel} from "components/Form"
import {KlassDropdown} from "components/Select"
import {BaseButton} from "components/buttons"
import {BaseDatePicker} from "components/DateTimePicker"
import {DEPENDENCY_STATUS_OPTIONS, ENROLLMENT_STATUS_OPTIONS, STATES_OPTIONS} from "types/students"
import styles from "./form.module.css"
import {Auth} from "types/auth"
import {
    checkPermission,
    getDegreeLevelName,
    getFieldLabel,
    getProgramOfTermInfo,
    getShortName,
    handleError,
    toDateOnlyString
} from "helpers"
import {fieldsService, finAidService, majorService, settingAcademicTrackService, userService} from "services"
import {BaseInput} from "components/inputs"
import {DisplayMode, SelectOption} from "types/common"
import cx from "classnames"
import {ActiveStudentContext} from "context/ActiveStudentContext"
import {DegreeLevel} from "types/degreeLevel"
import CampusSelect from "components/CampusSelect"
import ProgramSelect from "components/ProgramSelect"
import TermSelect from "components/TermSelect"
import produce from "immer"
import {Permissions} from "types/permission"
import {useAllEnrollmentTypes, useModel, useMutation} from "hooks"
import {useTranslation} from "react-i18next"
import {FinAidOffer, HousingPlan} from "types/fin-aid/fin-aid"
import {Settings} from "types/settings"
import {DepartmentCustomFields} from "./DepartmentCustomFields"
import {useQueryClient} from "@tanstack/react-query"

type Props = {
    onClickCancel: () => void
    onSaveSuccess: () => void
    displayMode?: DisplayMode
}

export function FinancialAidForm(props: Props) {
    const {t} = useTranslation(["common", "chat", "financialAid", "user"])
    const model = useModel()
    const {displayMode = "normal", onClickCancel, onSaveSuccess} = props
    const isFullDisplay = displayMode === "normal"
    const {student, setStudent, studentAdvisors, advisorsPopup, payingMethods} = useContext(ActiveStudentContext)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [majorVersionOptions, setMajorVersionOptions] = useState([])

    const [stateValue, setStateValue] = useState<SelectOption | undefined>()
    const [enrollmentTypeValue, setEnrollmentTypeValue] = useState<SelectOption | undefined>()
    const [enrollmentStatusValue, setEnrollmentStatusValue] = useState<SelectOption | undefined>()
    const [dependencyStatusValue, setDependencyStatusValue] = useState<SelectOption | undefined>()
    const [finAidOffer, setFinAidOffer] = React.useState<FinAidOffer | undefined>()
    const [academicTracks, setAcademicTracks] = useState<SelectOption[]>([])
    const [academicTrackValue, setAcademicTrackValue] = useState<SelectOption | undefined>()
    const [housingPlanValue, setHousingPlanValue] = useState<SelectOption | undefined>()
    const {enrollmentTypes} = useAllEnrollmentTypes()
    const [customFieldData, setCustomFieldData] = useState({})
    const queryClient = useQueryClient()

    const updateCustomFieldsForProfileMutation = useMutation(
        (data: Settings.UpdateCustomFieldsPayload) => fieldsService.updateCustomFieldsForProfile(data),
        {
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: [
                        "student-module-custom-fields-for-profile",
                        student.userId,
                        student.profileId,
                        "financial-aid"
                    ]
                })
            },
            onError: (error) => handleError(error)
        }
    )

    const housingPlanOptions = React.useMemo(
        () =>
            Object.values(HousingPlan).map((value) => ({
                id: value,
                name: t(`financialAid:housingPlan.${value}`)
            })),
        [t]
    )

    useEffect(() => {
        if (!student?.profileId) return

        setStateValue(STATES_OPTIONS.find((item) => item.id === student.profileState))
        setDependencyStatusValue(DEPENDENCY_STATUS_OPTIONS.find((item) => item.id === student.dependencyStatus))
        setEnrollmentStatusValue(ENROLLMENT_STATUS_OPTIONS.find((item) => item.id === student.enrollmentStatus))
        setHousingPlanValue(housingPlanOptions.find((item) => item.id === student.housingPlan))
        ;(async function loadLatestFinAidOffer() {
            setFinAidOffer(
                await finAidService.getFinAidLatestOffer({studentProfileId: student.profileId, linkedPackage: true})
            )
        })()
    }, [student?.profileId])

    useEffect(() => {
        if (!isEmpty(enrollmentTypes)) {
            const data = enrollmentTypes.find((item) => item.code === student.enrollmentType)
            if (data) {
                setEnrollmentTypeValue({id: data.code, name: data.name})
            }
        }
    }, [enrollmentTypes, student?.profileId])

    useEffect(() => {
        ;(async function getMajorVersionOptions() {
            if (!student?.currentMajor?.id) {
                setMajorVersionOptions([])
                return
            }
            try {
                const majorVersions = await majorService.getMajorVersions({
                    orderBy: "version_name",
                    orderDir: "ASC",
                    program_id: student.currentMajor.id,
                    isActive: true
                })
                setMajorVersionOptions(
                    majorVersions.map((item) => ({
                        ...item,
                        versionName: item.version_name,
                        versionCode: item.version_code
                    }))
                )
            } catch (error) {
                handleError(error)
                setMajorVersionOptions([])
            }
        })()
    }, [student?.currentMajor])

    useEffect(() => {
        if (!student?.startingTerm) return
        ;(async function updateStartingTermDates() {
            const {data: programsOfTerm} = await majorService.getMajorByTerm([student.startingTerm.id])
            setStudent((prev) =>
                produce(prev, (draft) => {
                    const isCurrentMajorExist =
                        !!draft.currentMajor?.id && programsOfTerm.some((major) => major.id === draft.currentMajor.id)
                    const programByTerm = getProgramOfTermInfo(programsOfTerm, draft)
                    if (!!programByTerm?.startDate) {
                        draft.startDate = toDateOnlyString(programByTerm.startDate)
                    }
                    if (!!programByTerm?.projectedEndDate) {
                        draft.contractEndDate = toDateOnlyString(programByTerm.projectedEndDate)
                        draft.projectedEndDate = toDateOnlyString(programByTerm.projectedEndDate)
                    }
                    if (!isCurrentMajorExist) {
                        draft.currentMajor = null
                        draft.currentMajorVersionId = null
                    }
                })
            )
        })()
    }, [student?.startingTerm])

    useEffect(() => {
        ;(async function getAcademicTrackOptions() {
            try {
                const {data} = await settingAcademicTrackService.getAllAcademicTracks({
                    sort: {orderBy: "name", orderDir: "asc"}
                })
                setAcademicTracks(data.map((track) => ({id: track.trackId, name: track.name})))
            } catch (error) {
                handleError(error)
            }
        })()
    }, [])

    useEffect(() => {
        setAcademicTrackValue(academicTracks.find((track) => track.id === student?.academicTrackId))
    }, [academicTracks, student?.academicTrackId])

    const onChangeData = (key, value) => {
        setStudent((prev) =>
            produce(prev, (draft) => {
                if (key === "currentMajor") {
                    draft.startingTermId = null
                    draft.startingTerm = null
                    draft.currentMajorVersionId = null
                    draft.degreeLevel = get(value, "degreeLevel.name", "")
                }
                draft[key] = value
            })
        )
    }

    const onClickSave = async () => {
        const customFieldsPayload: Settings.UpdateCustomFieldsPayload = {
            customFields: keys(customFieldData).map((key) => ({
                userId: student.userId,
                profileId: student.profileId,
                customFieldId: Number(key),
                value: customFieldData[key]
            }))
        }
        const dataSubmit: any = pick(student, [
            "inquiryMessage",
            "startDate",
            "contractEndDate",
            "firstDateOfClass",
            "enrollmentDocsCompletionDate",
            "midpointDate",
            "projectedEndDate",
            "actualEndDate",
            "numberOfDependents"
        ])

        if (dataSubmit?.numberOfDependents) {
            dataSubmit.numberOfDependents = Number(dataSubmit.numberOfDependents)
        }
        dataSubmit.type = Auth.UserProfileType.Student
        dataSubmit.enrollmentType = enrollmentTypeValue?.id
        dataSubmit.enrollmentStatus = enrollmentStatusValue?.id
        dataSubmit.dependencyStatus = dependencyStatusValue?.id
        dataSubmit.academicTrackId = academicTrackValue?.id
        dataSubmit.housingPlan = housingPlanValue?.id
        dataSubmit.campuses = (student.campuses || []).map((campus) => ({id: campus.id, name: campus.name}))
        dataSubmit.payingMethodIds = (student.payingMethods || []).map((payingMethod) => payingMethod.payingMethodId)
        dataSubmit.currentMajor = student.currentMajor ? get(student.currentMajor, "id") : null
        dataSubmit.currentMajorVersionId = student.currentMajorVersionId
            ? get(student.currentMajorVersionId, "id")
            : null
        dataSubmit.startingTermId = student.startingTerm ? get(student.startingTerm, "id") : null
        try {
            setIsSubmitting(true)
            await Promise.all([
                customFieldsPayload.customFields.length > 0
                    ? updateCustomFieldsForProfileMutation.mutateAsync(customFieldsPayload)
                    : Promise.resolve(),
                userService.updateProfileUserById(student.profileId, dataSubmit)
            ])
            onSaveSuccess()
        } catch (error) {
            handleError(error)
        } finally {
            setIsSubmitting(false)
        }
    }

    const checkHasPermissionToUpdateAdvisor = () => {
        return checkPermission({staff: [Permissions.Staff.FinancialAid.Students.UpdateAdvisor.Edit]}, model)
    }
    const hasPermissionToUpdateAdvisor = checkHasPermissionToUpdateAdvisor()

    const renderListAdvisors = () => {
        const studentAdvisorsOrderByDefault = orderBy(studentAdvisors, ["isDefault"], ["desc"])
        return (
            <div>
                {(studentAdvisorsOrderByDefault || []).map((advisor, index) => (
                    <span key={index} className={styles.advisorWrap}>
                        <span className={styles.advisorName}>{getShortName(advisor)}</span>
                        {index !== studentAdvisorsOrderByDefault.length - 1 && ", "}
                    </span>
                ))}
            </div>
        )
    }

    if (!student) {
        return null
    }

    return (
        <div className={cx({[styles.wrap]: isFullDisplay})}>
            {isFullDisplay && <p className={styles.title}>Student financial Aid Details</p>}
            <div className={styles.body}>
                <Row gutter={[24, 24]}>
                    <Col span={isFullDisplay ? 8 : 24}>
                        <div>
                            <FormLabel label="Dependency Status" />
                            <KlassDropdown
                                options={DEPENDENCY_STATUS_OPTIONS}
                                value={dependencyStatusValue}
                                onChange={setDependencyStatusValue}
                            />
                        </div>
                    </Col>
                    <Col span={isFullDisplay ? 8 : 24}>
                        <div>
                            <FormLabel label="Number of Dependents" />
                            <BaseInput
                                disabledArrow
                                className={styles.inputGrade}
                                type="number"
                                value={student.numberOfDependents}
                                onChange={(newValue) => onChangeData("numberOfDependents", newValue)}
                            />
                        </div>
                    </Col>
                    <Col span={isFullDisplay ? 8 : 24}>
                        <div>
                            <FormLabel label="Method of Paying for College" />
                            <KlassDropdown
                                isMulti
                                options={payingMethods}
                                valueKey="payingMethodId"
                                labelKey="name"
                                value={student.payingMethods}
                                onChange={(newValue) => onChangeData("payingMethods", newValue)}
                            />
                        </div>
                    </Col>
                    <Col span={isFullDisplay ? 8 : 24}>
                        <div>
                            <FormLabel label="Housing Plan" />
                            <KlassDropdown
                                options={housingPlanOptions}
                                value={housingPlanValue}
                                onChange={setHousingPlanValue}
                            />
                        </div>
                    </Col>
                    <Col span={isFullDisplay ? 8 : 24}>
                        <div>
                            <FormLabel label="Current Award year" />
                            <BaseInput
                                value={
                                    finAidOffer?.awardYear
                                        ? `${finAidOffer.awardYear} - ${finAidOffer.awardYear + 1}`
                                        : "-"
                                }
                                readOnly
                            />
                        </div>
                    </Col>
                    <Col span={isFullDisplay ? 8 : 24}>
                        <div>
                            <FormLabel label="Financial Aid Package status" />
                            <BaseInput
                                value={
                                    finAidOffer?.status
                                        ? String(t(`financialAid:finAidOffer.status.${finAidOffer?.status}`))
                                        : "-"
                                }
                                readOnly
                            />
                        </div>
                    </Col>
                    <Col span={isFullDisplay ? 8 : 24}>
                        <div>
                            <FormLabel label="Current ISIR" />
                            <BaseInput value={finAidOffer?.finAidPackage?.isir || "-"} readOnly />
                        </div>
                    </Col>
                </Row>

                <p className={styles.subTitle}>General</p>

                <Row gutter={[24, 24]}>
                    <Col span={isFullDisplay ? 8 : 24}>
                        <div>
                            <FormLabel label={t("studentInfo.advisor", {departmentName: "Admissions"})} />
                            {hasPermissionToUpdateAdvisor ? (
                                <div className={cx(styles.contentLink)} onClick={advisorsPopup.open}>
                                    {studentAdvisors && studentAdvisors.length
                                        ? renderListAdvisors()
                                        : t("studentInfo.selectAdvisor")}
                                </div>
                            ) : (
                                renderListAdvisors()
                            )}
                        </div>
                    </Col>
                    <Col span={isFullDisplay ? 8 : 24}>
                        <div>
                            <FormLabel label="Enrollment Type" />
                            <KlassDropdown
                                options={enrollmentTypes || []}
                                value={enrollmentTypeValue}
                                onChange={setEnrollmentTypeValue}
                            />
                        </div>
                    </Col>
                    <Col span={isFullDisplay ? 8 : 24}>
                        <div>
                            <FormLabel
                                label={getFieldLabel(
                                    model,
                                    Settings.GeneralLabel.EnrollmentStatus,
                                    "Enrollment status"
                                )}
                            />
                            <KlassDropdown
                                options={ENROLLMENT_STATUS_OPTIONS}
                                value={enrollmentStatusValue}
                                onChange={setEnrollmentStatusValue}
                            />
                        </div>
                    </Col>
                    <Col span={isFullDisplay ? 8 : 24}>
                        <div>
                            <FormLabel
                                label={getFieldLabel(model, Settings.GeneralLabel.Campuses, t("user:user.campuses"))}
                            />
                            <CampusSelect
                                isMulti
                                value={student.campuses}
                                onChange={(newValue) => onChangeData("campuses", newValue)}
                            />
                        </div>
                    </Col>
                    <Col span={isFullDisplay ? 8 : 24}>
                        <div>
                            <FormLabel label={t("user:user.currentMajor")} />
                            <ProgramSelect
                                isClearable
                                isActive
                                value={student.currentMajor}
                                onChange={(newValue) => onChangeData("currentMajor", newValue || null)}
                            />
                        </div>
                    </Col>
                    <Col span={isFullDisplay ? 8 : 24}>
                        <div>
                            <FormLabel
                                label={getFieldLabel(
                                    model,
                                    Settings.GeneralLabel.ProgramVersion,
                                    t("user:user.majorVersion")
                                )}
                            />
                            <KlassDropdown
                                onChange={(newValue) => onChangeData("currentMajorVersionId", newValue)}
                                getOptionLabel={({versionCode, versionName}) =>
                                    versionCode ? ` ${versionName} (${versionCode})` : versionName
                                }
                                getOptionValue={({versionCode, versionName}) =>
                                    versionCode ? ` ${versionName} (${versionCode})` : versionName
                                }
                                options={majorVersionOptions}
                                value={student.currentMajorVersionId}
                                isClearable
                            />
                        </div>
                    </Col>
                    <Col span={isFullDisplay ? 8 : 24}>
                        <div>
                            <FormLabel label={t("user:user.startTerm")} />
                            <TermSelect
                                isClearable
                                value={student.startingTerm}
                                onChange={(newValue) => onChangeData("startingTerm", newValue)}
                                majorId={get(student.currentMajor, "id") as number}
                                majorVersionId={get(student.currentMajorVersionId, "id") as number}
                                campusIds={student.campusIds}
                            />
                        </div>
                    </Col>

                    <Col span={isFullDisplay ? 8 : 24}>
                        <div>
                            <FormLabel
                                label={getFieldLabel(model, Settings.GeneralLabel.StartDate, t("user:user.startDate"))}
                            />
                            <BaseDatePicker value={student.startDate || null} dateOnly readOnly />
                        </div>
                    </Col>
                    <Col span={isFullDisplay ? 8 : 24}>
                        <div>
                            <FormLabel
                                label={getFieldLabel(
                                    model,
                                    Settings.GeneralLabel.ContractEndDate,
                                    t("user:user.contractEndDate")
                                )}
                            />
                            <BaseDatePicker value={student.contractEndDate || null} dateOnly readOnly />
                        </div>
                    </Col>
                    <Col span={isFullDisplay ? 8 : 24}>
                        <div>
                            <FormLabel
                                label={getFieldLabel(
                                    model,
                                    Settings.GeneralLabel.FirstDateOfClass,
                                    t("user:user.firstDateOfClass")
                                )}
                            />
                            <BaseDatePicker
                                value={student.firstDateOfClass || null}
                                dateOnly
                                onChange={(date) => onChangeData("firstDateOfClass", date)}
                            />
                        </div>
                    </Col>
                    <Col span={isFullDisplay ? 8 : 24}>
                        <div>
                            <FormLabel
                                label={getFieldLabel(
                                    model,
                                    Settings.GeneralLabel.EnrollmentDocsCompletionDate,
                                    t("user:user.enrollmentDocsCompletionDate")
                                )}
                            />
                            <BaseDatePicker
                                value={student.enrollmentDocsCompletionDate || null}
                                dateOnly
                                onChange={(date) => onChangeData("enrollmentDocsCompletionDate", date)}
                            />
                        </div>
                    </Col>
                    <Col span={isFullDisplay ? 8 : 24}>
                        <div>
                            <FormLabel
                                label={getFieldLabel(
                                    model,
                                    Settings.GeneralLabel.MidpointDate,
                                    t("user:user.midpointDate")
                                )}
                            />
                            <BaseDatePicker
                                value={student.midpointDate || null}
                                dateOnly
                                onChange={(date) => onChangeData("midpointDate", date)}
                            />
                        </div>
                    </Col>
                    <Col span={isFullDisplay ? 8 : 24}>
                        <div>
                            <FormLabel
                                label={getFieldLabel(
                                    model,
                                    Settings.GeneralLabel.ProjectedEndDate,
                                    t("user:user.projectedEndDate")
                                )}
                            />
                            <BaseDatePicker
                                value={student.projectedEndDate || null}
                                dateOnly
                                onChange={(date) => onChangeData("projectedEndDate", date)}
                            />
                        </div>
                    </Col>
                    <Col span={isFullDisplay ? 8 : 24}>
                        <div>
                            <FormLabel
                                label={
                                    getFieldLabel(
                                        model,
                                        Settings.GeneralLabel.ActualEndDate,
                                        t("user:user.actualEndDate")
                                    ) + " (System generated)"
                                }
                            />
                            <BaseDatePicker value={student.actualEndDate || null} dateOnly readOnly />
                        </div>
                    </Col>

                    <Col span={isFullDisplay ? 8 : 24}>
                        <div>
                            <FormLabel label="Degree Level" />
                            <BaseInput
                                value={
                                    student.degreeLevel
                                        ? typeof student.degreeLevel === "string"
                                            ? student.degreeLevel
                                            : getDegreeLevelName(student.degreeLevel as DegreeLevel.DegreeLevel)
                                        : null
                                }
                                readOnly
                            />
                        </div>
                    </Col>
                    <Col span={isFullDisplay ? 8 : 24}>
                        <div>
                            <FormLabel label="Academic Track" />
                            <KlassDropdown
                                options={academicTracks}
                                value={academicTrackValue}
                                onChange={setAcademicTrackValue}
                            />
                        </div>
                    </Col>
                </Row>
                <div className={styles.divider} />
                <DepartmentCustomFields
                    module={Settings.CustomFieldModule.FinancialAid}
                    userId={student.userId}
                    profileId={student.profileId}
                    isFullDisplay={isFullDisplay}
                    customFieldData={customFieldData}
                    setCustomFieldData={setCustomFieldData}
                    onChange={(customFieldId, value) =>
                        setCustomFieldData((prev) => ({...prev, [customFieldId]: value}))
                    }
                />
            </div>
            <div className={styles.action}>
                <BaseButton title="Cancel" variant="secondary" onClick={onClickCancel} />
                <BaseButton title="Save" loading={isSubmitting} onClick={onClickSave} />
            </div>
        </div>
    )
}
